import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DATE_FROM_PARAM, DATE_TO_PARAM } from '../../shared/use-query-params';
import { format, subDays } from 'date-fns';
import { DATE_FNS_GLOBAL_FORMAT } from '../../shared';

export const useModifyUrl = () => {
    const history = useHistory();
    const { search } = useLocation();

    const queryParams = useMemo(() => {
        const searchParams = new URLSearchParams(search);
        const stringParams = searchParams.toString();

        return stringParams ? `?${stringParams}` : '';
    }, [search]);

    const modifyUrl = useCallback(
        (param: string | undefined, parameterIndex: number) => {
            const currentPath = history.location.pathname;
            const pathSegments = currentPath.split('/');

            if (param) {
                if (pathSegments.length > parameterIndex) {
                    pathSegments[parameterIndex] = param;
                } else {
                    pathSegments.push(param);
                }
            } else {
                if (pathSegments.length > parameterIndex) {
                    pathSegments.splice(parameterIndex, 1);
                }
            }
            const newPath = pathSegments.join('/');
            history.push(`${newPath}${queryParams}`);
        },
        [history, queryParams],
    );

    return modifyUrl;
};

export const useModifySearchParameters = () => {
    const history = useHistory();
    const { search } = useLocation();

    const modifySearchParams = useCallback(
        (paramName: string, value: string | undefined) => {
            // create new URLSearchParams object from current search to avoid mutation issues
            const searchParams = new URLSearchParams(search);

            if (value) {
                // Encode the value in Base64
                const encodedValue = btoa(value);
                searchParams.set(paramName, encodedValue);
            } else {
                searchParams.delete(paramName);
            }

            const newSearch = searchParams.toString();
            //trim leading '?' if present
            const currentSearchNormalized = search.startsWith('?') ? search.substring(1) : search;

            // Only update if the search actually changed
            if (currentSearchNormalized !== newSearch) {
                history.replace({ search: newSearch });
            }
        },
        [history, search],
    );

    return modifySearchParams;
};

export const useDecodedSearchParams = () => {
    const { search } = useLocation();

    return useMemo(() => {
        const params = new URLSearchParams(search);
        return (paramName: string): string | null => {
            const encodedValue = params.get(paramName);
            if (!encodedValue) {
                return null;
            }

            // Decode the base64 encoded string
            return atob(encodedValue);
        };
    }, [search]);
};

const getDateFormatted = (intendedDate: Date | undefined, subDaysFromDate?: number | undefined) => {
    if (intendedDate) {
        const calculatedDate = subDaysFromDate ? subDays(intendedDate, subDaysFromDate) : intendedDate;
        return format(calculatedDate, DATE_FNS_GLOBAL_FORMAT);
    }
    return null;
};

export const dateRangeParamGenerator = (dateParam: Date, subtractedDays?: number): string => {
    return `?${DATE_FROM_PARAM}=${btoa(getDateFormatted(dateParam, subtractedDays) ?? '')}&${DATE_TO_PARAM}=${btoa(
        getDateFormatted(dateParam) ?? '',
    )}`;
};
